import React, { useEffect, useRef, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
  Grid,
} from '@mui/material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { useTranslation } from 'react-i18next';
import { getGeneralConfig, saveGeneralConfig } from '../../../services/ecommerceApi';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import {
  DEFAULT_STOREFRONT_FONT,
  STOREFRONT_FONT_PRESETS,
  ensureStorefrontFontPreviewLoaded,
  normalizeStorefrontFont,
} from './storefrontFontPresets';

interface FontFamilySettingsProps {
  companyId: string | null;
}

const PREVIEW_SAMPLE = 'Aa Bb Cc — Modern vitrin yazı tipi önizlemesi';

const FontFamilySettings: React.FC<FontFamilySettingsProps> = ({ companyId }) => {
  const { t } = useTranslation('salesSystem');
  const [fontFamily, setFontFamily] = useState(DEFAULT_STOREFRONT_FONT);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null
  );
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    ensureStorefrontFontPreviewLoaded();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        const response = await getGeneralConfig(companyId);
        if (response?.data) {
          setFontFamily(normalizeStorefrontFont(response.data.fontFamily));
        }
        loadedRef.current = true;
      } catch (error) {
        console.error('❌ [FONT-FAMILY] Ayarlar yüklenemedi:', error);
        setFeedback({ type: 'error', message: t('ecommerceGeneral.fontFamily.loadError') });
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [companyId, t]);

  const persist = async (nextFont: string) => {
    if (!companyId) return;
    const value = normalizeStorefrontFont(nextFont);
    setSaving(true);
    setFeedback(null);
    try {
      await saveGeneralConfig({ companyId, fontFamily: value });
      notifyEcommerceSaved(t('ecommerceGeneral.fontFamily.saveSuccess'));
    } catch (error) {
      console.error('❌ [FONT-FAMILY] Ayar kaydedilemedi:', error);
      setFeedback({ type: 'error', message: t('ecommerceGeneral.fontFamily.saveError') });
    } finally {
      setSaving(false);
    }
  };

  const schedulePersist = (nextFont: string) => {
    if (!loadedRef.current || !companyId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persist(nextFont), 400);
  };

  const applyFont = (value: string) => {
    setFontFamily(value);
    schedulePersist(value);
  };

  const activeFont = normalizeStorefrontFont(fontFamily);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TextFieldsIcon sx={{ mr: 1.5, color: 'primary.main' }} />
        <Typography variant="h6">{t('ecommerceGeneral.fontFamily.title')}</Typography>
        {saving && <CircularProgress size={18} sx={{ ml: 1.5 }} />}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceGeneral.fontFamily.help')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Typography variant="subtitle2" gutterBottom>
            {t('ecommerceGeneral.fontFamily.presetsLabel')}
          </Typography>

          <Grid container spacing={1.25} sx={{ mb: 2.5 }}>
            {STOREFRONT_FONT_PRESETS.map((preset) => {
              const selected = activeFont.toLowerCase() === preset.value.toLowerCase();
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={preset.key}>
                  <Button
                    fullWidth
                    variant={selected ? 'contained' : 'outlined'}
                    disabled={!companyId}
                    onClick={() => applyFont(preset.value)}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      alignItems: 'stretch',
                      flexDirection: 'column',
                      py: 1.25,
                      px: 1.5,
                      minHeight: 88,
                      borderColor: selected ? 'primary.main' : 'divider',
                      backgroundColor: selected ? 'primary.main' : '#fff',
                      color: selected ? '#fff' : 'text.primary',
                      boxShadow: selected ? 2 : 0,
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: selected ? 'primary.dark' : 'rgba(37, 99, 235, 0.04)',
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{
                        fontFamily: preset.value,
                        width: '100%',
                        textAlign: 'left',
                        mb: 0.35,
                        lineHeight: 1.25,
                      }}
                    >
                      {t(`ecommerceGeneral.fontFamily.presets.${preset.key}`)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: preset.value,
                        width: '100%',
                        textAlign: 'left',
                        opacity: selected ? 0.92 : 0.72,
                        lineHeight: 1.35,
                      }}
                    >
                      {PREVIEW_SAMPLE}
                    </Typography>
                  </Button>
                </Grid>
              );
            })}
          </Grid>

          <Box
            sx={{
              p: 2.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: '#fafafa',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('ecommerceGeneral.fontFamily.previewLabel')}
            </Typography>
            <Typography
              sx={{
                fontFamily: activeFont,
                fontSize: '1.65rem',
                fontWeight: 700,
                lineHeight: 1.25,
                color: '#0f172a',
                mb: 0.75,
              }}
            >
              {t('ecommerceGeneral.fontFamily.previewHeadline')}
            </Typography>
            <Typography
              sx={{
                fontFamily: activeFont,
                fontSize: '1rem',
                fontWeight: 500,
                color: '#334155',
                mb: 0.75,
                lineHeight: 1.5,
              }}
            >
              {t('ecommerceGeneral.fontFamily.previewSubline')}
            </Typography>
            <Typography
              sx={{
                fontFamily: activeFont,
                fontSize: '0.92rem',
                color: '#64748b',
                lineHeight: 1.6,
              }}
            >
              {t('ecommerceGeneral.fontFamily.previewBody')}
            </Typography>
          </Box>

          {feedback && (
            <Alert severity={feedback.type} sx={{ mt: 2 }}>
              {feedback.message}
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
};

export default FontFamilySettings;
